import { IonContent, IonLabel } from '@ionic/react';
import classNames from 'classnames';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { UiSvgIcon } from '@/components/ui/SvgIcon';
import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { queryParamsModule } from '@/modules/queryParams';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  LayoutBaseDataKeyType,
  LayoutBaseDataType,
} from '@/pages/Onboarding/types';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { onboardingInterestsOptions } from './constants';
import { ScreenInterestsStyles } from './Interests.styles';

export const OnboardingScreenInterests: React.FC = () => {
  const { wizard } = useOnboarding();

  const { watch, control } = useFormContext<LayoutBaseDataType>();
  const interests = watch('interests');

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Interests',
      });
    }
  }, []);

  const handleNextStep = () => {
    userService.createOrUpdate({
      onboarding_data: {
        interests: onboardingInterestsOptions
          .filter((option) => interests.includes(option.id))
          .map((option) => option.title),
      },
    });
    wizard.nextStep();
  };

  return (
    <ScreenInterestsStyles>
      <OnboardingNavigation />
      <OnboardingTitle>Tell us about your interests</OnboardingTitle>
      <OnboardingSubtitle>
        You can select more than one option:
      </OnboardingSubtitle>

      <IonContent>
        <div className="local-options">
          {onboardingInterestsOptions.map((option) => (
            <UiTypography
              key={option.id}
              className={classNames('local-options-item', 'ion-activatable', {
                checked: interests?.includes(option.id),
              })}
              component="label"
              variant="label-large"
            >
              <Controller
                control={control}
                name="interests"
                render={({ field }) => (
                  <input
                    onChange={(event) => {
                      const selectedInterests =
                        queryParamsModule.getParamsList<LayoutBaseDataKeyType>(
                          'interests',
                        );

                      if (event.target.checked) {
                        field.onChange([...field.value, option.id]);
                        selectedInterests.push(event.target.value);
                      } else {
                        field.onChange(
                          field.value.filter((x) => x !== option.id),
                        );
                        selectedInterests.splice(
                          selectedInterests.indexOf(event.target.value),
                          1,
                        );
                      }

                      queryParamsModule.setParamsList<LayoutBaseDataKeyType>(
                        'interests',
                        selectedInterests,
                      );
                    }}
                    name={field.name}
                    type="checkbox"
                    value={option.id}
                  />
                )}
              />
              <UiSvgIcon size="medium" symbol={option.id} className="icon" />
              <IonLabel>{option.title}</IonLabel>
            </UiTypography>
          ))}
        </div>
      </IonContent>

      <OnboardingFooter
        disabled={interests?.length === 0}
        onContinue={handleNextStep}
      />
    </ScreenInterestsStyles>
  );
};
